import { useCallback } from 'react';
import Form, { Rule } from 'epn-ui/umd/dist/components/Form';
import InputText from 'epn-ui/umd/dist/components/InputText';
import Select, { DefaultOptionType } from 'epn-ui/umd/dist/components/Select';
import Button from 'epn-ui/umd/dist/components/Button';
import Loader from 'epn-ui/umd/dist/components/Loader';

// Helpers
import useI18n from '@i18n';
import { IGetOffersDataFormatted, IGetTrafficSourcesDataFormatted } from '@api/types';

import {
    checkShortLinks,
    checkStoresUrl,
    compareUrlWithOffersUrl,
    validateSocialUrl,
    validateUrl,
} from './utils';

export interface IAddPlacementForm {
    url: string;
    offer: number | null;
    trafficSource: string | null;
}

export interface IPlacementAddFormProps {
    offers: IGetOffersDataFormatted[];
    trafficSources: IGetTrafficSourcesDataFormatted[];
    isLoading: boolean;
    createInProgress: boolean;
    onSubmit: (data: IAddPlacementForm) => void;
}

const DEFAULT_VALUES: IAddPlacementForm = {
    url: '',
    offer: null,
    trafficSource: null,
};

const AddPlacementForm: React.FC<IPlacementAddFormProps> = ({
    offers,
    trafficSources,
    isLoading,
    createInProgress,
    onSubmit,
}) => {
    const { t } = useI18n('Creatives');
    const { t: tTools } = useI18n('Tools');
    const { t: tCommon } = useI18n('Common');
    const [form] = Form.useForm<IAddPlacementForm>();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const filterOption = useCallback((input: string, option: any): boolean => {
        try {
            return option?.title?.toLowerCase().includes(input.toLowerCase());
        } catch {
            return false;
        }
    }, []);

    return (
        <Loader loading={isLoading}>
            <Form
                form={form}
                initialValues={{
                    url: DEFAULT_VALUES.url,
                    offer: DEFAULT_VALUES.offer,
                    trafficSources: DEFAULT_VALUES.trafficSource,
                }}
                onFinish={(values: IAddPlacementForm) => {
                    onSubmit(values);
                    form.resetFields();
                }}
                id="add placement modal"
            >
                <Form.Item
                    name="url"
                    label={t(
                        'Type a link to your traffic source (where you plan to direct users to the pages of the online store from)',
                    )}
                    rules={[
                        {
                            validator: async (_rule: Rule, value: string | null | undefined) => {
                                if (value === null || value === undefined || value === '') {
                                    throw new Error(`${t('Required field')}`);
                                }

                                // Отсеиваем мусорные строки
                                const isValidUrl = validateUrl(value, t);
                                if (!isValidUrl.valid) {
                                    throw new Error(isValidUrl.message);
                                }

                                const url: URL = new URL(value);

                                // Проверяем социальные сети
                                const isValidSocialUrl = validateSocialUrl(url, t);
                                if (!isValidSocialUrl.valid) {
                                    throw new Error(isValidSocialUrl.message);
                                }

                                // Сверим url со всеми офферами
                                const isOffer = await compareUrlWithOffersUrl(
                                    url,
                                    form.getFieldValue('offer'),
                                    t,
                                );
                                if (!isOffer.valid) {
                                    throw new Error(isOffer.message);
                                }

                                // Проверяем ссылки на магазины приложений
                                const isValidStoreUrl = checkStoresUrl(url, t);
                                if (!isValidStoreUrl.valid) {
                                    throw new Error(isValidStoreUrl.message);
                                }

                                // Отсеиваем короткие ссылки
                                const isFullUrl = checkShortLinks(url, t);
                                if (!isFullUrl.valid) {
                                    throw new Error(isFullUrl.message);
                                }

                                return true;
                            },
                            validateTrigger: 'onBlur',
                        },
                    ]}
                >
                    <InputText placeholder="https://site.ru" />
                </Form.Item>

                <Form.Item
                    name="offer"
                    label={tCommon('Choose offer')}
                    rules={[{ required: true, message: tCommon('Required field') }]}
                >
                    <Select
                        options={(offers as unknown) as DefaultOptionType[]}
                        placeholder={t('Start typing the name of the offer')}
                        disabled={!Array.isArray(offers) || offers.length === 0}
                        loading={isLoading}
                        fieldNames={{
                            label: 'title',
                            value: 'id',
                        }}
                        filterOption={filterOption}
                        notFoundContent={tCommon('Nothing found')}
                    />
                </Form.Item>

                <Form.Item
                    name="trafficSource"
                    label={t('Traffic source')}
                    rules={[{ required: true, message: tCommon('Required field') }]}
                >
                    <Select
                        options={(trafficSources as unknown) as DefaultOptionType[]}
                        placeholder={t('Choose the traffic source of your site')}
                        disabled={!Array.isArray(trafficSources) || trafficSources.length === 0}
                        loading={isLoading}
                        fieldNames={{
                            label: 'name',
                            value: 'id',
                        }}
                        notFoundContent={tCommon('Nothing found')}
                    />
                </Form.Item>

                <Form.Item className="w-full text-right xs:w-auto">
                    <Button
                        htmlType="submit"
                        type="primary"
                        className="w-full xs:w-auto"
                        disabled={isLoading || createInProgress}
                        loading={createInProgress}
                    >
                        {tTools('Add traffic source')}
                    </Button>
                </Form.Item>
            </Form>
        </Loader>
    );
};

export default AddPlacementForm;
